import {MouseEventHandler, ReactNode, Ref, useEffect, useState} from 'react';
import {ChangeHandler} from '../../types/Handler';
import IconButton from '../icon/IconButton';
import Paragraph from '../paragraph/Paragraph';
import {DfbCloseSymbol} from '../symbol/Symbol';

import {getStyle} from './InputHelper';

export type InputTypes = 'text' | 'email' | 'password' | 'number';
export type InputProps = {
    /**
     * html id of the inner <input /> element
     */
    id: string,
    type?: InputTypes,
    label: string | ReactNode,
    /** initial value */
    value?: string | number,
    maxInputLength?: number,
    errorMessage?: string,
    required?: boolean,
    disabled?: boolean,
    hidden?: boolean,
    className?: string,
    autoComplete?: string,
    onChange?: ChangeHandler,
    onClick?: MouseEventHandler<HTMLDivElement>,
    inputRef?: Ref<HTMLInputElement>
    dataInput?: boolean
    hideErrorMessage?: boolean
    tabIndex?: number
    min?: number,
    clearable?: boolean
};

const Input = (props: InputProps) => {
    const [focused, setFocused] = useState(false);
    const [value, setValue] = useState(props.value);

    const isValid = !props.errorMessage;
    let style = getStyle(props.disabled, props.hidden, focused, value, isValid);

    if (props.className) {
        style += ` ${props.className}`;
    }
    const onBlur = () => {
        setFocused(false);
    };

    const onChange = (newValue: string) => {
        setValue(newValue);

        if (props.onChange) {
            props.onChange(props.id, newValue);
        }
    };

    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    return (
        <div className={`dfb-Input${style}`}>
            <div className="dfb-Input-field">
                <label className="dfb-Input-label" htmlFor={props.id}>{props.label}</label>
                <input className="dfb-Input-input" autoComplete={props.autoComplete}
                       ref={props.inputRef}
                       type={props.type}
                       value={value ?? ''}
                       name="input"
                       id={props.id}
                       aria-invalid={!isValid}
                       onFocus={() => setFocused(true)}
                       onBlur={onBlur}
                       onChange={evt => onChange(evt.target.value)}
                       readOnly={props.disabled}
                       onClick={props.onClick}
                       required={props.required}
                       maxLength={props.maxInputLength}
                       data-input={props.dataInput}
                       tabIndex={props.tabIndex}
                       min={props.min}
                />
                {
                    props.clearable &&
                    <IconButton
                        onClick={() => onChange('')}
                        icon={DfbCloseSymbol}
                        className={`dfb-Input-reset ${!!value ? 'is-visible' : ''}`}
                    />
                }
            </div>
            {
                !isValid && !props.disabled && !props.hideErrorMessage &&
                <Paragraph size="small" className="dfb-Input-errorMessage">
                    {props.errorMessage}
                </Paragraph>
            }
        </div>
    );
};

Input.defaultProps = {
    autoComplete: 'new-password',
    type: 'text',
    disabled: false
};

export default Input;
